// Schermata «Richieste» (tela D9): le prenotazioni chieste da RVC dal ponte, con i controlli già fatti
// (posto libero all'ora chiesta, primo orario libero) e la risposta in un tocco: Conferma · Proponi · Rifiuta.
// La conferma apre il modulo della prenotazione già scritto: prenotazione e risposta si salvano insieme.
// Mai dati di salute qui: il questionario si compila in SPA (nel modulo, solo per il personale SPA).
import { doc, onSnapshot } from 'firebase/firestore';
import { t } from '../i18n.js';
import { h, sostituisci, app, chiudiFoglio, seguiInvio } from './dom.js';
import { oggiRoma, spostaGiorno, quandoBreve } from './date.js';
import { ascoltaGiornoIntero, ascoltaPrenotazione, creaPrenotazioni } from '../dati/prenotazioni.js';
import { OPERATRICI, toMin, hm, salaNome, adessoRoma, giornoBreve } from '../logica/agenda.js';
import { TRATTAMENTI } from '../logica/listino.js';
import { puoPrenotare } from '../logica/ruoli.js';
import { controllaRichiesta, ordinaRichieste, prenotazioniUguali } from '../logica/ponte.js';
import { apriModulo, apriScheda, testiInvio } from './prenotazione.js';
import { testata } from './schermate.js';

/**
 * @param {{ richieste: { lista: object[], ascolta: (f) => () => void }, ponte: object }} fonti
 *   richieste = elenco in tempo reale (lo tiene la barra, che conta anche quelle da rispondere) · ponte = collegamento
 */
export function schermataRichieste(ctx, dispositivo, { richieste, ponte }) {
  const ruoli = dispositivo.ruoli || [];
  const risponde = puoPrenotare(ruoli);
  const scritture = creaPrenotazioni({ ...ctx, nome: dispositivo.nome || '' });
  let lista = [];
  let nomi = {};
  let fuoco = oggiRoma();
  const giorni = new Map(); // data -> { dati, stop }
  const legate = new Map(); // id prenotazione -> { b, stop }
  // In attesa del database dopo un tocco: niente secondo tocco sulla stessa richiesta.
  const toccate = new Set();

  const sotto = h('p', { classe: 'sotto', id: 'rich-conta' });
  const statoPonte = h('p', { classe: 'stato-rete', id: 'rich-ponte', 'aria-live': 'polite' });
  const daFare = h('ul', { classe: 'rich-lista', id: 'rich-da-fare' });
  const fatte = h('ul', { classe: 'lista', id: 'rich-fatte' });

  const vista = {
    ctx, dispositivo, ruoli, scritture,
    data: () => fuoco,
    dati: () => giorni.get(fuoco)?.dati || null,
    nomi: () => nomi,
    vaiA: () => {},
  };
  const breve = (o) => nomi[o] || t('op.breve', { n: OPERATRICI.indexOf(o) + 1 });
  const vuota = (testo) => h('li', { classe: 'vuota' }, testo);

  function giornoTesto(k) {
    const oggi = oggiRoma();
    if (k === oggi) return t('ponte.oggi');
    if (k === spostaGiorno(oggi, 1)) return t('ponte.domani');
    return giornoBreve(k);
  }
  const quando = (k, ora) => `${giornoTesto(k)} ${hm(toMin(ora))}`;
  const chi = (r) => [r.camera ? t('ponte.camera', { n: r.camera }) : t('ponte.esterno'), r.nome].filter(Boolean).join(' · ');
  const trattamento = (r) => {
    const tr = TRATTAMENTI[r.tr];
    return [tr?.nome || r.tr, `${tr?.durata || r.durata}'`, r.persone === 2 ? t('ponte.coppia') : null].filter(Boolean).join(' · ');
  };
  const dove = (cs) => (cs.length === 2
    ? t('ponte.postoCoppia', { sala: salaNome(cs[0].sala), a: breve(cs[0].op), b: breve(cs[1].op) })
    : t('ponte.posto', { sala: salaNome(cs[0].sala), n: cs[0].lettino, op: breve(cs[0].op) }));

  // Dati dei giorni chiesti (prenotazioni, turni, pause, fuori servizio): servono ai controlli. Solo i giorni che servono.
  function seguiGiorni() {
    const servono = new Set(lista.filter((r) => r.stato === 'da-rispondere' && !r.errore).map((r) => r.data));
    for (const [k, g] of giorni) if (!servono.has(k)) { g.stop(); giorni.delete(k); }
    for (const k of servono) {
      if (giorni.has(k)) continue;
      const g = { dati: null, stop: () => {} };
      giorni.set(k, g);
      g.stop = ascoltaGiornoIntero(ctx, k, (x) => { g.dati = x; disegna(); }, (e) => ctx.scatola.registra(e, { dove: 'richieste-giorno' }));
    }
  }
  // Prenotazioni già legate a una richiesta (per una modifica o un annullamento di RVC).
  function seguiLegate() {
    const servono = new Set(lista.flatMap((r) => r.prenotazioni || []));
    for (const [id, x] of legate) if (!servono.has(id)) { x.stop(); legate.delete(id); }
    for (const id of servono) {
      if (legate.has(id)) continue;
      const x = { b: undefined, stop: () => {} };
      legate.set(id, x);
      x.stop = ascoltaPrenotazione(ctx, id, (b) => { x.b = b; disegna(); }, (e) => ctx.scatola.registra(e, { dove: 'richieste-legata' }));
    }
  }
  const legateDi = (r) => (r.prenotazioni || []).map((id) => legate.get(id)?.b).filter(Boolean);

  function rispondi(r, stato, opz = {}) {
    toccate.add(r.id);
    const x = scritture.rispondiPonte(r, stato, opz);
    seguiInvio(x.conferma, t(`ponte.salvata.${stato}`), testiInvio());
    x.conferma.then(() => { toccate.delete(r.id); disegna(); });
    disegna();
  }

  function conferma(r, posto) {
    fuoco = r.data;
    const [c1] = posto;
    const pre = { richiesta: r, data: r.data, inizio: r.inizio, sala: c1.sala, lettino: c1.lettino, op: c1.op };
    const leg = legateDi(r);
    apriModulo(vista, leg.length === 1 ? { ...pre, prenotazione: leg[0] } : pre);
  }

  // Rifiuta con un secondo tocco (niente finestre: sul telefono si tocca per sbaglio). Il primo tocco resta ricordato
  // per 4 secondi anche se la schermata si ridisegna nel frattempo.
  const sicuri = new Map(); // id richiesta -> timer
  function tastoRifiuta(r) {
    return h('button', {
      type: 'button', classe: 'secondario', dati: { azione: 'rifiuta' },
      onclick: () => {
        if (sicuri.has(r.id)) {
          clearTimeout(sicuri.get(r.id));
          sicuri.delete(r.id);
          rispondi(r, 'rifiutata');
          return;
        }
        sicuri.set(r.id, setTimeout(() => { sicuri.delete(r.id); disegna(); }, 4000));
        disegna();
      },
    }, sicuri.has(r.id) ? t('ponte.rifiutaSicuro') : t('ponte.rifiuta'));
  }

  function scheda(r) {
    const g = giorni.get(r.data)?.dati;
    const leg = legateDi(r);
    const uguali = leg.length && prenotazioniUguali(r, leg);
    const c = r.errore ? { esito: 'errore' } : g ? controllaRichiesta(r, g, adessoRoma()) : null;
    const controlli = [];
    const tasti = [];
    if (r.errore) controlli.push(h('li', { classe: 'e' }, t(`ponte.errore.${r.errore}`)));
    else if (!c) controlli.push(h('li', { classe: 'i' }, t('agenda.inCarico')));
    else if (uguali) controlli.push(h('li', { classe: 'ok' }, t('ponte.giaGiusta')));
    else if (c.esito === 'libero') controlli.push(h('li', { classe: 'ok' }, dove(c.posto)));
    else if (c.esito === 'passata') controlli.push(h('li', { classe: 'e' }, t('ponte.passata')));
    else controlli.push(h('li', { classe: 'w' }, t('ponte.occupato', { ora: hm(toMin(r.inizio)) })));
    if (c?.proposta?.length && !uguali) {
      controlli.push(h('li', { classe: 'i' }, t('ponte.primoLibero', { quando: quando(r.data, c.proposta[0].inizio), dove: dove(c.proposta) })));
    }
    if (r.prenotazioni?.length && !uguali && leg.length > 1) controlli.push(h('li', { classe: 'w' }, t('ponte.spostaCoppia')));
    if (risponde && c && !toccate.has(r.id)) {
      if (uguali) {
        tasti.push(h('button', { type: 'button', classe: 'primario', dati: { azione: 'conferma' }, onclick: () => rispondi(r, 'confermata', { prenotazioni: r.prenotazioni }) }, t('ponte.conferma')));
      } else if (c.esito === 'libero' && leg.length <= 1) {
        tasti.push(h('button', { type: 'button', classe: 'primario', dati: { azione: 'conferma' }, onclick: () => conferma(r, c.posto) }, t('ponte.conferma')));
      }
      if (c.proposta?.length && !uguali) {
        const p = { data: r.data, inizio: c.proposta[0].inizio };
        tasti.push(h('button', {
          type: 'button', classe: tasti.length ? 'secondario' : 'primario', dati: { azione: 'proponi' },
          onclick: () => rispondi(r, 'altro-orario', { proposta: p }),
        }, t('ponte.proponi', { ora: hm(toMin(p.inizio)) })));
      }
      if (leg.length) tasti.push(h('button', { type: 'button', classe: 'secondario', dati: { azione: 'apri' }, onclick: () => { fuoco = leg[0].data; apriScheda(vista, leg[0].id); } }, t('ponte.apri')));
      tasti.push(tastoRifiuta(r));
    }
    return h('li', { classe: 'richiesta', dati: { id: r.id } },
      h('div', { classe: 'testa' },
        h('b', {}, chi(r)),
        h('small', {}, t('ponte.arrivata', { ora: quandoBreve(r.arrivata) || '…', chi: r.daNome || 'RVC' }))),
      h('p', { classe: 'tr' }, trattamento(r)),
      h('p', { classe: 'quando' }, h('b', {}, r.errore === 'data' || r.errore === 'ora' ? '—' : quando(r.data, r.inizio)),
        r.prima ? h('span', { classe: 'piccolo' }, t('ponte.prima', { quando: quando(r.prima.data, r.prima.inizio) })) : null),
      r.pagamento !== 'pagamento' ? h('p', { classe: 'piccolo' }, t(`tipo.${r.pagamento}`)) : null,
      r.esterno && (r.esterno.tel || r.esterno.email) ? h('p', { classe: 'piccolo' }, [r.esterno.tel, r.esterno.email].filter(Boolean).join(' · ')) : null,
      r.nota ? h('p', { classe: 'nota' }, r.nota) : null,
      h('ul', { classe: 'msgs controlli' }, controlli),
      tasti.length ? h('div', { classe: 'azioni' }, tasti) : null);
  }

  // Già risposte: lo stato e se RVC l'ha già ricevuta (S1: «⏳» finché il database non conferma).
  function riga(r) {
    const invio = r.inSospeso ? t('ponte.sulTelefono') : r.invio?.mandato ? t('ponte.mandata') : t('ponte.inInvio');
    const testo = r.stato === 'altro-orario' && r.proposta
      ? t('ponte.stato.proposta', { quando: quando(r.proposta.data, r.proposta.inizio) })
      : t(`ponte.stato.${r.stato}`);
    const vive = legateDi(r).filter((b) => b.stato !== 'disdetta' && b.stato !== 'no-show');
    return h('li', { classe: 'riga', dati: { id: r.id, stato: r.stato } },
      h('span', { classe: 'cresce' },
        h('span', { classe: 'nome' }, `${chi(r)} · ${quando(r.data, r.inizio)}`),
        h('span', { classe: 'piccolo' }, `${trattamento(r)} · ${r.stato === 'annullata' ? '' : invio}`.replace(/ · $/, ''))),
      h('span', { classe: `chip ponte-${r.stato}` }, testo),
      r.stato === 'annullata' && vive.length ? h('button', {
        type: 'button', classe: 'secondario', dati: { azione: 'apri' }, onclick: () => { fuoco = vive[0].data; apriScheda(vista, vive[0].id); },
      }, t('ponte.disdici')) : null);
  }

  function disegna() {
    const { daFare: df, altre } = ordinaRichieste(lista);
    sotto.textContent = t('ponte.conta', { n: df.length });
    const s = ponte.stato();
    statoPonte.textContent = !s.acceso ? t('ponte.spento')
      : s.ok === false ? t('ponte.nonRaggiungibile')
        : s.ultimo ? t('ponte.controllato', { ora: new Date(s.ultimo).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }) })
          : '';
    sostituisci(daFare, df.length ? df.map(scheda) : vuota(t('ponte.nessuna')));
    sostituisci(fatte, altre.length ? altre.map(riga) : vuota(t('ponte.nessunaFatta')));
  }

  const stopLista = richieste.ascolta((x) => { lista = x; seguiGiorni(); seguiLegate(); disegna(); });
  const stopStruttura = onSnapshot(doc(ctx.db, `strutture/${ctx.sid}`), (d) => {
    nomi = (d.exists() && d.data().operatrici) || {};
    disegna();
  }, (e) => ctx.scatola.registra(e, { dove: 'struttura' }));
  const stopPonte = ponte.ascolta(disegna);

  sostituisci(app(), h('main', { classe: 'pagina richieste-pagina', id: 'richieste-pagina' },
    testata(t('ponte.luogo')),
    h('h1', { classe: 'data' }, t('ponte.titolo')),
    sotto, statoPonte, daFare,
    h('h2', { classe: 'sez' }, t('ponte.fatte')),
    fatte,
    h('p', { classe: 'hint centrato' }, t('ponte.regola24'))));
  disegna();
  // I controlli dipendono dall'ora (una richiesta di oggi può diventare «passata»): ridisegno ogni minuto.
  const orologio = setInterval(disegna, 60_000);
  return () => {
    clearInterval(orologio);
    for (const x of sicuri.values()) clearTimeout(x);
    chiudiFoglio();
    stopLista();
    stopStruttura();
    stopPonte();
    for (const g of giorni.values()) g.stop();
    for (const x of legate.values()) x.stop();
  };
}
